import React, { useEffect, useState, useRef, useCallback } from 'react';
import Nav from '../landing/nav';
import { FiActivity, FiUsers, FiCheckCircle, FiClock, FiAlertCircle, FiWifi, FiWifiOff, FiAward } from 'react-icons/fi';

const ElectionReturn = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState(false);
  const [pstTime, setPstTime] = useState(null);
  
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // --- Philippine Standard Time (PST) Logic ---
  useEffect(() => {
    let timer;
    const fetchPST = async () => {
      try {
        const response = await fetch('https://timeapi.io/api/Time/current/zone?timeZone=Asia/Manila');
        const timeData = await response.json();
        
        // Use dateTime for precision, fallback to system clock if API fails
        let currentTime = new Date(timeData.dateTime);

        timer = setInterval(() => {
          currentTime = new Date(currentTime.getTime() + 1000);
          setPstTime(currentTime.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: true 
          }));
        }, 1000);
      } catch (err) {
        console.error("Failed to sync with PST Server:", err);
        timer = setInterval(() => {
          setPstTime(new Date().toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit', 
            hour12: true 
          }));
        }, 1000);
      }
    };

    fetchPST();
    return () => clearInterval(timer);
  }, []);

  // --- WebSocket Connection Logic ---
  const connectWebSocket = useCallback(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) return;

    const socket = new WebSocket('https://54zlq9v9-8080.asse.devtunnels.ms/');

    socket.onopen = () => {
      setConnected(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    socket.onmessage = (event) => {
      try {
        const jsonData = JSON.parse(event.data);
        if (jsonData.type === 'election_data') {
          setData(jsonData.payload);
        }
      } catch (err) {
        console.error("Error parsing election response:", err);
      }
    };

    socket.onclose = () => {
      setConnected(false);
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 2000);
    };

    socket.onerror = () => socket.close();
    socketRef.current = socket;
  }, []);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (socketRef.current) socketRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  // --- Winner Logic Helper ---
  const getWinnerCount = (position) => {
    const pos = position.toUpperCase();
    if (pos.includes('P.I.O')) return 2;
    if (pos.includes('REPRESENTATIVE')) return 4;
    return 1; // Default for Pres, VP, Sec, Treas, Aud, Prot. Officer
  };

  // --- Loading State ---
  if (!connected && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 text-white font-['Poppins']">
        <FiActivity className="w-20 h-20 animate-pulse text-blue-500 mb-6" />
        <h2 className="text-3xl font-black tracking-widest uppercase text-center px-4">Waiting for data...</h2>
        <div className="flex flex-col items-center gap-2 mt-4 text-slate-500 font-bold uppercase tracking-widest text-xs text-center">
          <p>PST: {pstTime || "Syncing Clock..."}</p>
          <div className="flex items-center gap-2">
            <FiWifiOff className="animate-bounce" />
            <p>Retrying connection every 2s...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-950 min-h-screen text-slate-100 font-['Poppins'] selection:bg-blue-500">
      <Nav menuOpen={menuOpen} toggleMenu={() => setMenuOpen(!menuOpen)} />
      
      <div className="max-w-7xl mx-auto px-4 pt-32 pb-12">
        
        {/* Real-time Status Banner */}
        <div className="bg-slate-900 border-2 border-slate-800 rounded-3xl p-8 mb-8 shadow-2xl relative overflow-hidden">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 relative z-10">
            <div>
              <div className="flex flex-wrap items-center gap-4 mb-2">
                <div className="flex items-center gap-2 text-blue-400 font-black text-xs uppercase bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                  <FiClock /> PST: {pstTime || "Loading..."}
                </div>
                <div className="text-slate-500 font-bold text-xs uppercase tracking-widest">
                  Last Transmitted: {data?.lastUpdate}
                </div>
              </div>
              
              <h1 className="text-4xl font-black text-white leading-tight">
                {data?.isWaiting ? (
                  <span className="text-indigo-400 animate-pulse uppercase">Awaiting Transmission</span>
                ) : data?.processedReturns < data?.totalReturns ? (
                  <span className="text-yellow-500 animate-pulse uppercase">ER IS NOW BEING TRANSMITTED</span>
                ) : (
                  <span className="text-green-500 uppercase">Final Election Return</span>
                )}
              </h1>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <span className={`flex items-center gap-2 px-6 py-2 rounded-full font-black text-xs border-2 transition-all ${connected ? 'bg-green-500/10 border-green-500 text-green-500' : 'bg-red-500/10 border-red-500 text-red-500 animate-pulse'}`}>
                {connected ? <><FiWifi /> LIVE FEED</> : <><FiWifiOff /> LINK DOWN - RETRYING</>}
              </span>
              <span className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Status: {data?.status}</span>
            </div>
          </div>
        </div>

        {/* Dash Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <StatBox icon={<FiUsers />} label="Total Voters" value={data?.totalVoters} sub="Eligible Population" color="blue" />
          <StatBox icon={<FiCheckCircle />} label="Returns Processed" value={`${data?.processedReturns}/${data?.totalReturns}`} sub="Precinct Tally" color="green" />
          <StatBox icon={<FiAlertCircle />} label="Abstain Count" value={data?.overallAbstain} sub="Blank Ballots" color="red" />
          <StatBox icon={<FiActivity />} label="Participation" value={`${((data?.processedReturns / (data?.totalReturns || 1)) * 100).toFixed(0)}%`} sub="Current Turnout" color="purple" />
        </div>

        {/* Position Tally Cards */}
        <div className="grid grid-cols-1 gap-10">
          {Object.entries(data?.results || {}).map(([position, candidates]) => {
            const totalPosVotes = Object.values(candidates).reduce((a, b) => a + b, 0);
            const winnerCount = getWinnerCount(position);
            const sortedCandidates = Object.entries(candidates).sort(([, a], [, b]) => b - a);

            return (
              <div key={position} className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-lg">
                <div className="bg-slate-800/50 px-8 py-5 border-b border-slate-800">
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight">{position}</h2>
                </div>
                
                <div className="p-8 space-y-8">
                  {sortedCandidates.map(([name, votes], index) => {
                    const percentage = totalPosVotes > 0 ? ((votes / totalPosVotes) * 100).toFixed(1) : 0;
                    const isAbstain = name === "ABSTAIN";
                    // Highlight logic: Not abstain, has votes, and is in the top N slots
                    const isWinner = !isAbstain && votes > 0 && index < winnerCount;

                    return (
                      <div key={name} className={`relative transition-all duration-500 ${isWinner ? 'scale-[1.01]' : ''}`}>
                        <div className="flex justify-between items-end mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`text-xl font-black transition-colors ${isWinner ? 'text-yellow-400' : isAbstain ? 'text-slate-600' : 'text-white'}`}>
                              {name}
                            </span>
                            {isWinner && (
                              <span className="flex items-center gap-1 bg-yellow-500/20 text-yellow-500 text-[10px] font-black px-2 py-0.5 rounded-md border border-yellow-500/30 uppercase tracking-tighter">
                                <FiAward className="w-3 h-3" /> Leading
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`text-2xl font-black ${isWinner ? 'text-yellow-400' : 'text-blue-500'}`}>
                              {votes.toLocaleString()}
                            </span>
                            <span className="text-sm font-black text-slate-400 ml-3">{percentage}%</span>
                          </div>
                        </div>
                        <div className="h-5 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700">
                          <div 
                            className={`h-full transition-all duration-1000 ease-in-out ${
                              isWinner 
                                ? 'bg-gradient-to-r from-yellow-600 to-amber-400 shadow-[0_0_15px_rgba(234,179,8,0.3)]' 
                                : isAbstain ? 'bg-slate-700' : 'bg-gradient-to-r from-blue-600 to-indigo-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ---
const StatBox = ({ icon, label, value, sub, color }) => {
  const colorMap = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    red: 'text-red-500',
    purple: 'text-purple-500'
  };

  return (
    <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl hover:border-slate-700 transition-all shadow-md">
      <div className={`text-3xl mb-4 ${colorMap[color] || 'text-blue-500'}`}>{icon}</div>
      <div className="text-slate-500 font-black text-xs uppercase tracking-widest mb-1">{label}</div>
      <div className="text-3xl font-black text-white">{value || "0"}</div>
      <div className="text-[10px] text-slate-700 font-bold uppercase mt-2 tracking-widest">{sub}</div>
    </div>
  );
};

export default ElectionReturn;